<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.network-rtk.delaysChart"
					descriptionKey="stats.network-rtk.delaysChartTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<TimeSeriesChart :isLoading="isLoadingAggregated" :series="delaysAggregatedChartSeries" :options="delaysAggregatedChartOptions" />

				<StatsChartTitleWithDescription
					titleKey="stats.network-rtk.vrsCountChart"
					descriptionKey="stats.network-rtk.vrsCountChartTooltip"
				/>
				<TimeSeriesChart :isLoading="isLoadingAggregated" :series="vrsCountAggregatedChartSeries" :options="vrsCountAggregatedChartOptions" />

				<StatsChartTitleWithDescription
					:titleKey="brdcCssrChartTitleKey"
					:descriptionKey="brdcCssrChartTooltipKey"
				/>
				<TimeSeriesChart :isLoading="isLoadingAggregated" :series="brdcCssrAggregatedChartSeries" :options="brdcCssrAggregatedChartOptions" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Affix :offsetTop="77" :target="() => wrapper">
					<Card :bordered="false">
						<VerticalLayout>
							<SubNetworkSelect ref="subNetworkSelect" :onChange="onChangeDatesRange" :allSubnetsOption="true" />
							<DateRangeChooser v-model="datesRange" @change="onChangeDatesRange" />
							<VerticalLayout v-if="isAggregated" class="pd-vertical">
								<LabeledSwitch v-model="showMin" :rightLabel="$t('controls.switches.showMin')" />
								<LabeledSwitch v-model="showMax" :rightLabel="$t('controls.switches.showMax')" />
							</VerticalLayout>
						</VerticalLayout>
					</Card>
				</Affix>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { Row, Col, Card, Affix } from 'ant-design-vue';

import Api from '@/api/Api';

import { useSessionStore } from '@/stores/session';
import { useLicenseStore } from '@/stores/licenseStore';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { useFrozenSubNetworks } from '@/components/common/useFrozenSubNetworks';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter, getSubNetworkLabel } from '@/utils/chartsUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';

import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';

import { useProvideInjectWrapper } from '@/utils/useProvideInject';


const getSubNetworkSeriesKey = (subNetworks, period) => item => {
	const snLabel = getSubNetworkLabel(subNetworks, item.sub_network);
	if (period > 1) {
		return snLabel + '-' + item.agg_function;
	}
	return snLabel;
};
const getMountpointSeriesKey = (period) => item => {
	if (period > 1 && item.agg_function) {
		return item.mountpoint + '-' + item.agg_function;
	}
	return item.mountpoint;
};
const getSecondsValue = item => item.final_value / 1000;
const getTrafficValue = item => item.final_value / 1024;
const getIntValue = item => item.final_value;

export default {
	components: {
		Row, Col, Card, Affix,
		VerticalLayout, DateRangeChooser, TimeSeriesChart, StatsChartTitleWithDescription, LabeledSwitch,
		SubNetworkSelect,
	},
	setup() {
		const { apiData, isLoading: isLoadingAggregated, doRequest } = Api.useVrsStatsAggregatedGet();
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const { injectValue } = useProvideInjectWrapper;
		const wrapper = injectValue();
		const showMin = ref(true);
		const showMax = ref(true);
		const isAggregated = computed(() => apiData.value.period > 1);
		const subNetworkSelect = ref();

		const onChangeDatesRange = () => {
			if (datesRangeStr.value != null) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
					show_min: showMin.value,
					show_max: showMax.value,
					sub_network_id: subNetworkSelect.value.selectedId,
				});
			}
		};

		watch([showMin, showMax], () => {
			onChangeDatesRange();
		});

		const { subNetworks } = useFrozenSubNetworks();
		const licenseStore = useLicenseStore();

		const delaysAggregatedChartSeries = computed(() => generateSeries({
			items: apiData.value.vrsItems.filter(item => item.kind === 'delay'),
			getSeriesKeyFn: getSubNetworkSeriesKey(subNetworks.value, apiData.value.period),
			getValueFn: getSecondsValue,
			seriesStep: apiData.value.period,
		}).series);

		const delaysAggregatedChartOptions = computed(() => {
			let options = getBaseOptions('delaysAggregated', delaysAggregatedChartSeries.value, apiData.value.funcCount);
			options.yaxis.title.text = 'seconds';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(3) + ' s');
			return options;
		});

		const vrsCountAggregatedChartSeries = computed(() => generateSeries({
			items: apiData.value.vrsItems.filter(item => item.kind === 'vrsCount'),
			getSeriesKeyFn: getSubNetworkSeriesKey(subNetworks.value, apiData.value.period),
			getValueFn: getIntValue,
			seriesStep: apiData.value.period,
		}).series);

		const vrsCountAggregatedChartOptions = computed(() => {
			let options = getBaseOptions('vrsCountAggregated', vrsCountAggregatedChartSeries.value, apiData.value.funcCount);
			options.yaxis.title.text = 'VRS count';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value));
			return options;
		});

		const brdcCssrAggregatedChartSeries = computed(() => generateSeries({
			items: apiData.value.brdcCssrItems.filter(item => item.kind === 'inTrafficDiff'),
			getSeriesKeyFn: getMountpointSeriesKey(apiData.value.period),
			getValueFn: getTrafficValue,
			seriesStep: apiData.value.period,
		}).series);

		const brdcCssrAggregatedChartOptions = computed(() => {
			let options = getBaseOptions('brdcCssrAggregated', brdcCssrAggregatedChartSeries.value, apiData.value.funcCount);
			options.yaxis.title.text = 'Kbytes / min';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value) + ' KB/min');
			return options;
		});

		const brdcCssrChartTitleKey = computed(() => 'stats.network-rtk.' + (licenseStore.allowPppRtk ? 'brdcCssrChart' : 'brdcChart'));
		const brdcCssrChartTooltipKey = computed(() => 'stats.network-rtk.' + (licenseStore.allowPppRtk ? 'brdcCssrChartTooltip' : 'brdcChartTooltip'));

		onMounted(() => {
			onChangeDatesRange();
		});
		return {
			isLoadingAggregated,
			subNetworkSelect,
			datesRange,
			wrapper,
			showMin,
			showMax,
			isAggregated,
			delaysAggregatedChartSeries,
			delaysAggregatedChartOptions,
			vrsCountAggregatedChartSeries,
			vrsCountAggregatedChartOptions,
			brdcCssrAggregatedChartSeries,
			brdcCssrAggregatedChartOptions,
			brdcCssrChartTitleKey,
			brdcCssrChartTooltipKey,

			onChangeDatesRange,
		};
	},
}
</script>

